import { z } from 'zod';

/**
 * Shop-wide display settings — one global choice, not a per-user preference. With a
 * handful of people looking at the same board, two people seeing different sizes is
 * more confusing than a size nobody individually picked.
 *
 * Starts with `fontScale`; more display knobs can join this same document later.
 */
export const appearance = z.object({
  /**
   * Multiplies the root font size. Tailwind's default type scale and spacing are both
   * rem-based, so this grows padding and gaps along with text instead of just making
   * the type cramped — the same technique as a browser's own zoom.
   */
  fontScale: z.number().min(0.8).max(2).default(1),
  /**
   * The shop's configured timezone — read-only here. It is server config
   * (`TZ_SHOP`/`TZ`), not stored data, because it is "the single place the clock
   * enters the app" for scheduling: `ctx.today()` resolves through it on every
   * request, so changing it live, without a restart, is a bigger change than a
   * display setting ought to make lightly. Shown here so it doesn't take a shell on
   * the server to find, and so every screen can render "today" consistently instead
   * of guessing from whichever device is looking at it.
   */
  timeZone: z.string(),
});
export type Appearance = z.infer<typeof appearance>;

export const appearanceWrite = z.object({
  fontScale: appearance.shape.fontScale,
});
export type AppearanceWrite = z.infer<typeof appearanceWrite>;

/** Presets offered in the UI — plain numbers underneath, so a custom value entered by
 * editing the API directly still round-trips even if it does not match one exactly. */
export const FONT_SCALE_PRESETS = [
  { label: 'Normal', value: 1 },
  { label: 'Large', value: 1.15 },
  { label: 'Extra large', value: 1.3 },
  { label: 'Huge', value: 1.5 },
] as const;
